import { PLACES_CATEGORIES } from "@/utilities/constants/PlacesCategories";

// Comprehensive category mappings for plural variations and common alternatives
const CATEGORY_MAPPINGS: Record<string, string> = {
  // === FOOD & DINING ===
  // Restaurants
  restaurants: "Restaurant",
  restaurant: "Restaurant",

  // Cafes
  cafes: "Cafe",
  cafe: "Cafe",
  coffee: "Cafe",
  "coffee shop": "Cafe",
  "coffee shops": "Cafe",

  // Bakeries
  bakeries: "Bakery",
  bakery: "Bakery",

  // Bars
  bars: "Bar",
  bar: "Bar",
  pub: "Bar",
  pubs: "Bar",

  // === RETAIL ===
  // Banks
  banks: "Bank",
  bank: "Bank",
  atm: "Bank",

  // Stores
  stores: "Store",
  store: "Store",
  shop: "Store",
  shops: "Store",

  // Markets
  markets: "Market",
  market: "Market",
  supermarket: "Market",
  supermarkets: "Market",

  // Malls
  malls: "Mall",
  mall: "Mall",
  "shopping center": "Mall",
  "shopping centres": "Mall",

  // === EDUCATION ===
  // Schools
  schools: "School",
  school: "School",

  // Universities
  universities: "University",
  university: "University",
  college: "University",
  colleges: "University",

  // Libraries
  libraries: "Library",
  library: "Library",

  // === HEALTHCARE ===
  // Hospitals
  hospitals: "Hospital",
  hospital: "Hospital",

  // Clinics
  clinics: "Clinic",
  clinic: "Clinic",

  // Pharmacies
  pharmacies: "Pharmacy",
  pharmacy: "Pharmacy",
  "drug store": "Pharmacy",
  "drug stores": "Pharmacy",

  // === ACCOMMODATION ===
  // Hotels
  hotels: "Hotel",
  hotel: "Hotel",
  motel: "Hotel",
  motels: "Hotel",

  // === ENTERTAINMENT & CULTURE ===
  // Museums
  museums: "Museum",
  museum: "Museum",

  // Theaters
  theaters: "Theater",
  theater: "Theater",
  theatre: "Theater",
  theatres: "Theater",
  cinema: "Theater",
  cinemas: "Theater",
  "movie theater": "Theater",
  "movie theaters": "Theater",

  // Gyms
  gyms: "Gym",
  gym: "Gym",
  "fitness center": "Gym",
  "fitness centres": "Gym",

  // Spas
  spas: "Spa",
  spa: "Spa",

  // Salons
  salons: "Salon",
  salon: "Salon",
  "beauty salon": "Salon",
  "hair salon": "Salon",

  // Galleries
  galleries: "Gallery",
  gallery: "Gallery",
  "art gallery": "Gallery",

  // Clubs
  clubs: "Club",
  club: "Club",
  nightclub: "Club",
  nightclubs: "Club",

  // === RECREATION ===
  // Parks
  parks: "Park",
  park: "Park",

  // Beaches
  beaches: "Beach",
  beach: "Beach",

  // Pools
  pools: "Pool",
  pool: "Pool",
  "swimming pool": "Pool",

  // Sports Facilities
  "basketball courts": "Basketball Court",
  "basketball court": "Basketball Court",
  "tennis courts": "Tennis Court",
  "tennis court": "Tennis Court",
  "football fields": "Football Field",
  "football field": "Football Field",
  "soccer fields": "Football Field",
  "soccer field": "Football Field",

  // === TRANSPORTATION ===
  // Stations
  stations: "Station",
  station: "Station",
  "train station": "Station",
  "railway station": "Station",
  "bus station": "Station",

  // Airports
  airports: "Airport",
  airport: "Airport",

  // === RELIGIOUS ===
  // Churches
  churches: "Church",
  church: "Church",

  // Temples
  temples: "Temple",
  temple: "Temple",

  // Mosques
  mosques: "Mosque",
  mosque: "Mosque",

  // === BUSINESS ===
  // Offices
  offices: "Office",
  office: "Office",
  "business office": "Office",

  // Buildings
  buildings: "Building",
  building: "Building",
  "office building": "Building",

  // Centers
  centers: "Center",
  center: "Center",
  centres: "Centre",
  centre: "Centre",
  "business center": "Center",
  "business centre": "Centre",

  // === SERVICES ===
  // Auto Services
  "auto repair": "Auto Repair Shop",
  "auto repair shops": "Auto Repair Shop",
  "car repair": "Auto Repair Shop",
  mechanic: "Auto Repair Shop",
  mechanics: "Auto Repair Shop",

  "car dealers": "Car Dealer",
  "car dealer": "Car Dealer",
  dealership: "Car Dealer",
  dealerships: "Car Dealer",

  "bmw showroom": "BMW Showroom",
  "bmw showrooms": "BMW Showroom",

  // Books
  bookstores: "Bookstore",
  bookstore: "Bookstore",
  "book shop": "Bookstore",
  "book shops": "Bookstore",

  books: "Bookstore",

  // === EDUCATION INSTITUTIONS ===
  "boarding schools": "Boarding School",
  "boarding school": "Boarding School",

  "british council": "British Council School Centre",
  "british council schools": "British Council School Centre",

  // === SPECIALTY STORES ===
  "bridal shops": "Bridal Shop",
  "bridal shop": "Bridal Shop",

  "acrylic stores": "Acrylic Store",
  "acrylic store": "Acrylic Store",

  "baking supplies": "Baking Supply Store",
  "baking supply": "Baking Supply Store",
  "baking supply stores": "Baking Supply Store",

  "bean sprout": "Bean Sprout & Soybean Sprout",
  "bean sprouts": "Bean Sprout & Soybean Sprout",
  "soybean sprout": "Bean Sprout & Soybean Sprout",
  "soybean sprouts": "Bean Sprout & Soybean Sprout",

  // === FACILITIES ===
  bridges: "Bridge",
  bridge: "Bridge",

  "business parks": "Business Park",
  "business park": "Business Park",

  "business centres": "Business Centre",

  courts: "Court",
  court: "Court",

  fields: "Field",
  field: "Field",

  grounds: "Ground",
  ground: "Ground",

  studios: "Studio",
  studio: "Studio",
  "art studio": "Studio",

  // === GEOGRAPHIC ===
  attractions: "Attraction",
  attraction: "Attraction",
  "tourist attraction": "Attraction",
  "tourist attractions": "Attraction",
};

// Normalize string for comparison
function normalizeString(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();
}

// Advanced plural to singular conversion with mapping support
function mapToCanonical(input: string): string {
  const normalized = normalizeString(input);

  // Check explicit mappings first
  if (CATEGORY_MAPPINGS[normalized]) {
    return CATEGORY_MAPPINGS[normalized];
  }

  // Basic plural rules as fallback
  if (normalized.endsWith("ies")) {
    return normalized.slice(0, -3) + "y";
  }
  if (normalized.endsWith("es")) {
    return normalized.slice(0, -2);
  }
  if (normalized.endsWith("s") && normalized.length > 1) {
    return normalized.slice(0, -1);
  }

  return normalized;
}

// Check if category matches (case-insensitive, with plural and alternative support) - DEPRECATED
// Use enhanced isValidCategory function below with fuzzy matching support

// Map input category to correct category name - DEPRECATED
// Use enhanced mapCategory function below with fuzzy matching support

// Get mapping explanation for user feedback
export function getCategoryMappingExplanation(inputCategory: string): string {
  const mappedCategory = mapCategory(inputCategory);

  if (!mappedCategory) {
    return `"${inputCategory}" is not a valid category`;
  }

  if (normalizeString(inputCategory) === normalizeString(mappedCategory)) {
    return `"${inputCategory}" is valid`;
  }

  return `"${inputCategory}" → "${mappedCategory}"`;
}

// Get all valid categories for reference
export function getValidCategories(): string[] {
  return [...PLACES_CATEGORIES].sort();
}

// Get all supported mappings
export function getSupportedMappings(): Array<{
  input: string;
  output: string;
}> {
  return Object.entries(CATEGORY_MAPPINGS)
    .filter(([key, value]) => PLACES_CATEGORIES.includes(value))
    .map(([input, output]) => ({ input, output }))
    .sort((a, b) => a.output.localeCompare(b.output));
}

// Calculate Levenshtein distance between two strings
function levenshteinDistance(a: string, b: string): number {
  const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }

  return matrix[b.length][a.length];
}

// Calculate similarity percentage between two strings (0-100)
function calculateSimilarity(str1: string, str2: string): number {
  const normalized1 = normalizeString(str1);
  const normalized2 = normalizeString(str2);

  const distance = levenshteinDistance(normalized1, normalized2);
  const maxLength = Math.max(normalized1.length, normalized2.length);

  if (maxLength === 0) return 100;

  return Math.round(((maxLength - distance) / maxLength) * 100);
}

// Find best matching category with fuzzy matching
export function findBestMatchingCategory(inputCategory: string, threshold: number = 70): string | null {
  if (!inputCategory?.trim()) return null;

  let bestMatch: string | null = null;
  let bestScore = 0;

  // First try direct match with mappings (use original functions to avoid circular dependency)
  const directMatch = findDirectMatch(inputCategory);
  if (directMatch) return directMatch;

  // Try fuzzy matching with all predefined categories
  for (const category of PLACES_CATEGORIES) {
    const similarity = calculateSimilarity(inputCategory, category);

    if (similarity >= threshold && similarity > bestScore) {
      bestScore = similarity;
      bestMatch = category;
    }
  }

  // Try fuzzy matching with all mapping keys
  for (const [mappingKey, mappedCategory] of Object.entries(CATEGORY_MAPPINGS)) {
    if (PLACES_CATEGORIES.includes(mappedCategory)) {
      const similarity = calculateSimilarity(inputCategory, mappingKey);

      if (similarity >= threshold && similarity > bestScore) {
        bestScore = similarity;
        bestMatch = mappedCategory;
      }
    }
  }

  return bestMatch;
}

// Helper function for direct matching (to avoid circular dependency)
function findDirectMatch(inputCategory: string): string | null {
  if (!inputCategory?.trim()) return null;

  const normalizedInput = normalizeString(inputCategory);

  // Direct match with predefined categories (case-insensitive)
  const directMatch = PLACES_CATEGORIES.find(
    (category) => normalizeString(category) === normalizedInput
  );

  if (directMatch) return directMatch;

  // Try mapping to canonical form
  const canonicalForm = mapToCanonical(normalizedInput).toLowerCase();
  const canonicalMatch = PLACES_CATEGORIES.find(
    (category) => normalizeString(category) === canonicalForm
  );

  return canonicalMatch || null;
}

// Enhanced isValidCategory with fuzzy matching support
export function isValidCategory(inputCategory: string): boolean {
  if (!inputCategory?.trim()) return false;

  const normalizedInput = normalizeString(inputCategory);

  // Direct match with predefined categories (case-insensitive)
  const directMatch = PLACES_CATEGORIES.some(
    (category) => normalizeString(category) === normalizedInput
  );

  if (directMatch) return true;

  // Try mapping to canonical form
  const canonicalForm = mapToCanonical(normalizedInput).toLowerCase();
  const canonicalMatch = PLACES_CATEGORIES.some(
    (category) => normalizeString(category) === canonicalForm
  );

  if (canonicalMatch) return true;

  // Try fuzzy matching with 70% threshold
  const fuzzyMatch = findBestMatchingCategory(inputCategory, 70);
  return fuzzyMatch !== null;
}

// Enhanced mapCategory with fuzzy matching support
export function mapCategory(inputCategory: string): string | null {
  if (!inputCategory?.trim()) return null;

  const normalizedInput = normalizeString(inputCategory);

  // Direct match with predefined categories (case-insensitive)
  const directMatch = PLACES_CATEGORIES.find(
    (category) => normalizeString(category) === normalizedInput
  );

  if (directMatch) return directMatch;

  // Try mapping to canonical form
  const canonicalForm = mapToCanonical(normalizedInput).toLowerCase();
  const canonicalMatch = PLACES_CATEGORIES.find(
    (category) => normalizeString(category) === canonicalForm
  );

  if (canonicalMatch) return canonicalMatch;

  // Try fuzzy matching with 70% threshold
  return findBestMatchingCategory(inputCategory, 70);
}

// Get similarity score for debugging and user feedback
export function getCategorySimilarityScore(inputCategory: string, targetCategory: string): number {
  return calculateSimilarity(inputCategory, targetCategory);
}

// Process category for CSV import - returns matched category if >= 70% similarity, otherwise null
export function processCategoryForCSV(inputCategory: string): string | null {
  if (!inputCategory?.trim()) return null;

  // Try to find the best match with 70% threshold
  const bestMatch = findBestMatchingCategory(inputCategory, 70);
  return bestMatch;
}

// Get detailed matching information for user feedback
export function getCategoryMatchDetails(inputCategory: string): {
  isValid: boolean;
  matchedCategory?: string;
  similarityScore?: number;
  matchType?: 'direct' | 'canonical' | 'fuzzy' | 'none';
  explanation: string;
} {
  if (!inputCategory?.trim()) {
    return {
      isValid: false,
      matchType: 'none',
      explanation: 'Empty category'
    };
  }

  const normalizedInput = normalizeString(inputCategory);

  // Check direct match
  const directMatch = PLACES_CATEGORIES.find(
    (category) => normalizeString(category) === normalizedInput
  );
  if (directMatch) {
    return {
      isValid: true,
      matchedCategory: directMatch,
      matchType: 'direct',
      similarityScore: 100,
      explanation: `"${inputCategory}" is a valid category`
    };
  }

  // Check canonical mapping
  const canonicalForm = mapToCanonical(normalizedInput);
  const canonicalMatch = PLACES_CATEGORIES.find(
    (category) => normalizeString(category) === canonicalForm.toLowerCase()
  );
  if (canonicalMatch) {
    return {
      isValid: true,
      matchedCategory: canonicalMatch,
      matchType: 'canonical',
      similarityScore: 100,
      explanation: `"${inputCategory}" maps to "${canonicalMatch}"`
    };
  }

  // Check fuzzy match
  const fuzzyMatch = findBestMatchingCategory(inputCategory, 70);
  if (fuzzyMatch) {
    const similarity = getCategorySimilarityScore(inputCategory, fuzzyMatch);
    return {
      isValid: true,
      matchedCategory: fuzzyMatch,
      matchType: 'fuzzy',
      similarityScore: similarity,
      explanation: `"${inputCategory}" fuzzy matches "${fuzzyMatch}" (${similarity}% similarity)`
    };
  }

  return {
    isValid: false,
    matchType: 'none',
    explanation: `"${inputCategory}" is not a valid category and no similar match found`
  };
}

// Test fuzzy matching functionality
export function testFuzzyMatching(): void {
  console.log("=== Fuzzy Matching Test ===");

  const testCases = [
    // Should match with high similarity
    { input: "Restaur", expected: "Restaurant", threshold: 85 },
    { input: "Caffee", expected: "Cafe", threshold: 85 },
    { input: "Bakry", expected: "Bakery", threshold: 80 },
    { input: "Hospitl", expected: "Hospital", threshold: 85 },
    { input: "Scool", expected: "School", threshold: 80 },

    // Should match with moderate similarity
    { input: "Rstrnt", expected: "Restaurant", threshold: 75 },
    { input: "cofeeshop", expected: "Cafe", threshold: 70 },
    { input: "markt", expected: "Market", threshold: 75 },
    { input: "libary", expected: "Library", threshold: 80 },

    // Should not match (below threshold)
    { input: "xyzabc", expected: null, threshold: 70 },
    { input: "qwerty", expected: null, threshold: 70 },
    { input: "completelyrandom", expected: null, threshold: 70 },

    // Edge cases
    { input: "", expected: null, threshold: 70 },
    { input: "   ", expected: null, threshold: 70 },
  ];

  console.log("Input".padEnd(20) + "Expected".padEnd(15) + "Actual".padEnd(15) + "Similarity".padEnd(12) + "Result");
  console.log("-".repeat(80));

  testCases.forEach(({ input, expected, threshold }) => {
    const result = findBestMatchingCategory(input, threshold);
    const similarity = input ? getCategorySimilarityScore(input, result || "") : 0;
    const status = result === expected ? "✅ PASS" : "❌ FAIL";

    console.log(
      `${(input || "empty").padEnd(20)}${(expected || "null").padEnd(15)}${(result || "null").padEnd(15)}${similarity.toString().padEnd(12)}${status}`
    );
  });
}

// Test CSV processing functionality
export function testCSVProcessing(): void {
  console.log("=== CSV Processing Test ===");

  const testData = [
    "Restaurant",
    "restaur",  // typo
    "Cafe",
    "cofeeshop", // typo
    "Bakery",
    "bakry",    // typo
    "Invalid Category",
    "randomtext",
    "", // empty
  ];

  console.log("Original".padEnd(20) + "Processed".padEnd(15) + "Match Type".padEnd(12) + "Status");
  console.log("-".repeat(60));

  testData.forEach(category => {
    const processed = processCategoryForCSV(category);
    const details = getCategoryMatchDetails(category);
    const status = processed ? "✅ Matched" : "❌ Rejected";

    console.log(
      `${(category || "empty").padEnd(20)}${(processed || "null").padEnd(15)}${(details.matchType || "none").padEnd(12)}${status}`
    );
  });
}

// Test the mapping function
export function testCategoryMappings(): void {
  const testCases = [
    // Direct matches
    "Restaurant",
    "Cafe",
    "Bakery",

    // Case variations
    "restaurant",
    "RESTAURANTS",
    "cafe",
    "CAFES",

    // Plural variations
    "restaurants",
    "cafes",
    "bakeries",
    "bars",
    "banks",

    // Common alternatives
    "coffee shop",
    "pub",
    "supermarket",
    "movie theater",
    "coffee",
    "atm",

    // Invalid cases
    "invalid category",
    "unknown place",
  ];

  console.log("=== Category Mapping Test ===");
  console.log(
    "Input".padEnd(25) +
      "→".padEnd(5) +
      "Mapped To".padEnd(20) +
      "| Explanation"
  );
  console.log("-".repeat(70));

  testCases.forEach((testCase) => {
    const result = mapCategory(testCase);
    const explanation = getCategoryMappingExplanation(testCase);
    console.log(
      `${testCase.padEnd(25)} → ${result || "null".padEnd(20)} | ${explanation}`
    );
  });
}

// Export mappings for documentation
export const CATEGORY_MAPPING_EXAMPLES = [
  { from: "restaurants", to: "Restaurant", type: "plural" },
  { from: "cafes", to: "Cafe", type: "plural" },
  { from: "bakeries", to: "Bakery", type: "plural" },
  { from: "coffee shop", to: "Cafe", type: "alternative" },
  { from: "pub", to: "Bar", type: "alternative" },
  { from: "supermarket", to: "Market", type: "alternative" },
  { from: "atm", to: "Bank", type: "alternative" },
  { from: "movie theater", to: "Theater", type: "alternative" },
  { from: "RESTAURANTS", to: "Restaurant", type: "case" },
  { from: "gym", to: "Gym", type: "direct" },
];
